<template>
  <section-pdf-viewer :section-doc="section"
                      dom-ref="PAIE_VIEWER"
                      :dialog="dialog"
                      :extended="extended"
                      :maximise="!dialog"
                      :fullscreen="dialog"
                      :mode="mode"
                      :show-modif="showModif"
                      :show-chat="showChat"
                      :sub-section="subSection"
                      @extended="setExtended"
                      @close-dialog="closeDialog"
                      @close-sub-dialog="closeSubDialog">
  </section-pdf-viewer>
</template>

<script lang="ts">
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { storeContrat, storeDocument, storeSession, storeChat } from '@/generated/stores';
  import SectionPdfViewer from '@/framework/document/components/SectionPdfViewer.vue';
  import Button from '@/framework/base/classes/Button';
  import User from '@/generated/classes/User';
  import Document, { DocumentModes, ObjType } from '@/generated/classes/Document';
  import UserProfile, { EnumProfile } from "@/framework/base/classes/UserProfile";
  import Paie, { PaieStatuts } from '@/generated/classes/Paie';
  import Contrat, { ContratStatuts } from '@/generated/classes/Contrat';
  import Section from '@/framework/base/classes/Section';
  import SectionRemuneration from '@/custom/sections/SectionRemuneration.vue';
  import { MapRessource } from '@/framework/base/helpers/Ressources';
  import { Extended } from '@/framework/base/classes/Schema';
  import {
    AppButtons,
    AppSections,
    FwkButtons,
    RessourceAppButton,
    RessourceAppSection,
    RessourceFwkButton
  } from '@/ressources/globalRessources';
  import { defineComponent, type PropType } from "vue";
  import Files, { DownloadData } from "@/generated/classes/Files.ts";
  import { getBlob } from "@/framework/files/helpers/FileTypes.ts";

  export default defineComponent({
    name: 'SectionPaieViewer',
    components: {
      SectionPdfViewer
    },
    props: {
      paie: {
        type: Object as PropType<Paie>,
        required: true,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      extended: {
        type: Boolean,
        required: false,
      },
    },
    data () {
      const modeLabel = ['Détaillé', 'Simplifié'];
      return {
        lastPaieId: 0,
        subSection: new Section() as Section,
        mode: 0,
        showModif: false,
        showChat: false,
        sectionType: 'paie',
        modeLabel,
        modeLabel1: modeLabel[0],
        modeLabel2: modeLabel[1],
      };
    },
    computed: {
      ressource (): MapRessource {
        return RessourceAppSection(AppSections.PAIE_VIEWER);
      },
      isSalarie (): boolean {
        let res = false;
        const user = storeSession.connectedUser
        if (user && user.id > 0) {
          res = user.id == this.paie.salarieId;
        }
        return res;
      },
      contrat (): Contrat {
        if (this.paie.contratId) {
          return storeContrat.getContrat(this.paie.contratId);
        } else {
          return new Contrat();
        }
      },
      isSupport (): boolean {
        const user = storeSession.connectedUser as User
        return (user && UserProfile.hasProfile(user, EnumProfile.support));
      },
      document (): Document {
        let res = new Document();
        const document = storeDocument.listDocument.find(a => a.objType == ObjTypes.PAIE && a.objId == this.paie.id);
        if (document) {
          res = document;
        } else {
          const objType = new ObjType()
          objType.type = ObjTypes.PAIE;
          objType.id = this.paie.id;
          Document.getByObjType(objType).then((doc) => {
            res = doc;
          });
        }
        this.mode = DocumentModes.NORMAL;
        return res;
      },
      section (): Section {
        if (this.paie.id != this.lastPaieId) {
          this.sectionType = 'paie';
          this.lastPaieId = this.paie.id;
        }
        const section = new Section();
        section.type = this.sectionType;
        if (this.sectionType == 'paie') {
          section.objType = ObjTypes.PAIE;
          section.objId = this.document.id;
          section.ressource = this.ressource;
          section.picto = 'SectionPictoPaie';
          section.data = this.paie;
          section.buttons = this.buttons;
          section.chat = true;
          section.unreadChatCount = this.unreadChatCount;
          section.chatLabel = this.chatLabel;
        } else {
          section.ressource = this.ressource;
          section.picto = 'SectionPictoPaie';
          section.chat = false;
          section.data = this.paie;
          section.buttons = this.buttons;
        }
        return section;
      },
      unreadChatCount (): number {
        let count = 0;
        if (this.paie.chatId > 0) {
          const chat = storeChat.getChat(this.paie.chatId);
          if (chat) {
            count = chat.unreadCount;
          }
        }
        return count;
      },
      chatLabel (): string {
        if (!this.isSalarie) {
          return "Chat pour discuter avec votre salarié";
        } else {
          return "Chat pour discuter avec votre employeur";
        }
      },
      buttons (): Button[] {
        const buttons: Button[] = [];
        // Vue support
        if (this.isSupport) {
          {
            const button = new Button();
            button.show = true;
            button.label = this.modeLabel1;
            button.outlined = true;
            button.disabled = false;
            button.callback = this.displayMode1;
            buttons.push(button);
          }
          {
            const button = new Button();
            button.show = true;
            button.label = this.modeLabel2;
            button.outlined = true;
            button.disabled = false;
            button.callback = this.displayMode2;
            buttons.push(button);
          }
        }
        // Vue  
        else if (!this.isSalarie) {
          if (this.paie.statut < PaieStatuts.APPROVED) {
            if (this.contrat.statut >= ContratStatuts.APPROUVED || this.paie.toSubmit) {
              const button = new Button();
              button.show = true;
              button.label = RessourceAppButton(AppButtons.VARIABLES).label;
              button.outlined = true;
              button.disabled = false;
              button.callback = this.showVariables;
              buttons.push(button);
            }
          } else if (this.paie.statut == PaieStatuts.APPROVED) {
            const button = new Button();
            button.show = true;
            button.label = RessourceFwkButton(FwkButtons.PAYER).label;
            button.disabled = false;
            button.callback = this.payerPaie;
            buttons.push(button);
          } else if (this.paie.statut == PaieStatuts.COUNTED) {
            {
              const button = new Button();
              button.show = true;
              button.label = this.modeLabel1;
              button.outlined = true;
              button.disabled = false;
              button.callback = this.displayMode1;
              buttons.push(button);
            }
          } else {
            const button = new Button();
            button.show = true;
            button.label = RessourceFwkButton(FwkButtons.FERMER).label;
            button.callback = this.FermerDialog;
            button.disabled = false;
            buttons.push(button);
          }
          // Vue salarié
        } else {
          this.mode = DocumentModes.NORMAL;
          if (this.paie.hasSalarieUpdate) {
            const button = new Button();
            button.show = true;
            button.label = RessourceAppButton(this.showModif ? AppButtons.DOCUMENT : AppButtons.REVISION).label;
            button.outlined = true;
            button.disabled = false;
            button.callback = this.displayRevision;
            buttons.push(button);
          }
        }

        if (this.paie.statut == PaieStatuts.PAYED || this.paie.statut == PaieStatuts.COUNTED) {
          {
            const button = new Button();
            button.show = true;
            button.label = RessourceFwkButton(FwkButtons.DOWNLOAD_DOCUMENT).label;
            button.callback = this.DownloadDocument;
            button.disabled = false;
            buttons.push(button);
          }
        }
        return buttons;
      },
      payed (): boolean {
        return this.paie.statut == PaieStatuts.PAYED;
      },
    },
    created (): void {
      this.mode = DocumentModes.NORMAL;
    },
    methods: {
      DownloadDocument (): Promise<void> {
        return new Promise((resolve) => {
          this.document.mode = this.mode;
          let fileName = this.document.fileName;
          const data = new DownloadData();
          data.service = 'DOWNLOAD';
          data.type.extension = this.document.fileExtension;
          data.data = JSON.stringify(this.document);
          Files.download(data)
            .then((buffer) => {
              const blob = getBlob(buffer, data.type.extension);
              const file = window.URL.createObjectURL(blob);
              const docUrl = document.createElement('a');
              if (!fileName.endsWith(data.type.extension)) {
                fileName = `${fileName}.${data.type.extension}`
              }
              docUrl.setAttribute('href', file);
              docUrl.setAttribute('download', fileName);
              document.body.appendChild(docUrl);
              docUrl.click();
            });
          resolve();
        });
      },
      FermerDialog (): Promise<void> {
        return new Promise((resolve) => {
          this.closeDialog();
          resolve();
        });
      },
      modifyPaie (): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          Paie.modify(this.paie).then(() => {
            resolve();
          });
        });
      },
      payerPaie (): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          Paie.pay(this.paie).then(() => {
            resolve();
          }).catch(() => {
            resolve();
          });
        });
      },
      showVariables (): Promise<void> {
        return new Promise((resolve) => {
          const contrat = storeContrat.getContrat(this.paie.contratId);
          this.subSection.component = SectionRemuneration;
          this.subSection.type = 'contrat';
          this.subSection.data = contrat;
          this.subSection.propType = 'paie';
          this.subSection.propData = this.paie;
          resolve();
        });
      },
      commentPaie (): Promise<void> {
        return new Promise((resolve) => {
          this.showChat = true;
          resolve();
        });
      },
      displayRevision (): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          this.showModif = !this.showModif;
          resolve();
        });
      },
      displayMode1 (): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          this.setModes(0);
          resolve();
        });
      },
      displayMode2 (): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          this.setModes(1);
          resolve();
        });
      },
      setModes (index: number) {
        if (this.modeLabel[index] == 'Détaillé') {
          this.mode = DocumentModes.DETAIL;
          this.modeLabel1 = 'Simplifié';
        } else if (this.modeLabel[index] == 'Simplifié') {
          this.mode = DocumentModes.NORMAL;
          this.modeLabel1 = 'Détaillé';
        }
        this.modeLabel[0] = this.modeLabel1;
      },
      setExtended (extended: Extended): void {
        this.$emit('extended', extended);
      },
      closeDialog (): void {
        this.$emit('closeDialog');
      },
      closeSubDialog (): void {
        this.showChat = false;
        this.subSection = new Section();
      },
    },
  });
</script>